import React, { useState, useEffect } from 'react';
import { Layout, Tabs, Card, Button, Table, Tag, Space, Modal, Form, Input, Select, DatePicker, message, Typography, Row, Col, Statistic, Empty, Upload, Menu } from 'antd';
import { PlusOutlined, FileTextOutlined, TeamOutlined, CalendarOutlined, UploadOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, SearchOutlined, EyeOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import Sidebar from './Sidebar';
import moment from 'moment';

const { Header, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const Recruitment = () => {
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const [activeTab, setActiveTab] = useState('jobs');
    const [loading, setLoading] = useState(false);

    // Data states
    const [jobs, setJobs] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [interviews, setInterviews] = useState([]);
    const [interviewers, setInterviewers] = useState([]);

    // Modal states
    const [jobModalVisible, setJobModalVisible] = useState(false);
    const [candidateModalVisible, setCandidateModalVisible] = useState(false);
    const [interviewModalVisible, setInterviewModalVisible] = useState(false);
    
    const [form] = Form.useForm();
    const [candidateForm] = Form.useForm();
    const [interviewForm] = Form.useForm();

    const [selectedJob, setSelectedJob] = useState(null);
    const [selectedCandidate, setSelectedCandidate] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [jobsRes, candidatesRes, interviewsRes] = await Promise.all([
                api.get('/admin/recruitment/jobs'),
                api.get('/admin/recruitment/candidates'),
                api.get('/admin/recruitment/interviews')
            ]);
            
            if (jobsRes.data.success) setJobs(jobsRes.data.jobs);
            if (candidatesRes.data.success) setCandidates(candidatesRes.data.candidates);
            if (interviewsRes.data.success) setInterviews(interviewsRes.data.interviews);
        } catch (error) {
            message.error('Failed to fetch recruitment data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // Also fetch staff for interviewer dropdown
        api.get('/admin/staff').then(res => {
            if (res.data.success) setInterviewers(res.data.staff);
        });
    }, []);

    const handleCreateJob = async (values) => {
        try {
            const endpoint = selectedJob ? `/admin/recruitment/jobs/${selectedJob.id}` : '/admin/recruitment/jobs';
            const method = selectedJob ? 'put' : 'post';
            
            const res = await api[method](endpoint, values);
            if (res.data.success) {
                message.success(`Job ${selectedJob ? 'updated' : 'created'} successfully`);
                setJobModalVisible(false);
                setSelectedJob(null);
                form.resetFields();
                fetchData();
            }
        } catch (error) {
            message.error('Operation failed');
        }
    };

    const handleAddCandidate = async (values) => {
        try {
            const formData = new FormData();
            Object.keys(values).forEach(key => {
                if (key === 'resume' && values[key] && values[key].length > 0) {
                    formData.append('resume', values[key][0].originFileObj);
                } else if (key !== 'resume') {
                    formData.append(key, values[key]);
                }
            });

            const res = await api.post('/admin/recruitment/candidates', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (res.data.success) {
                message.success('Candidate added successfully');
                setCandidateModalVisible(false);
                candidateForm.resetFields();
                fetchData();
            }
        } catch (error) {
            message.error('Failed to add candidate');
        }
    };

    const handleUpdateStatus = async (id, status) => {
        try {
            const res = await api.put(`/admin/recruitment/candidates/${id}/status`, { status });
            if (res.data.success) {
                message.success('Status updated');
                fetchData();
            }
        } catch (error) {
            message.error('Update failed');
        }
    };

    const handleScheduleInterview = async (values) => {
        try {
            const res = await api.post('/admin/recruitment/interviews', {
                ...values,
                scheduledAt: values.scheduledAt.toDate()
            });
            if (res.data.success) {
                message.success('Interview scheduled');
                setInterviewModalVisible(false);
                interviewForm.resetFields();
                fetchData();
            }
        } catch (error) {
            message.error('Scheduling failed');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
    };

    const jobColumns = [
        { title: 'Job Title', dataIndex: 'title', key: 'title', render: text => <strong>{text}</strong> },
        { title: 'Location', dataIndex: 'location', key: 'location' },
        { title: 'Type', dataIndex: 'jobType', key: 'jobType', render: type => <Tag color="blue">{type}</Tag> },
        { title: 'Status', dataIndex: 'status', key: 'status', render: status => (
            <Tag color={status === 'OPEN' ? 'green' : 'red'}>{status}</Tag>
        )},
        { title: 'Actions', key: 'actions', render: (_, record) => (
            <Button size="small" onClick={() => { setSelectedJob(record); form.setFieldsValue(record); setJobModalVisible(true); }}>Edit</Button>
        )}
    ];

    const candidateColumns = [
        { title: 'Name', dataIndex: 'name', key: 'name', render: (text, r) => (
            <Space direction="vertical" size={0}>
                <strong>{text}</strong>
                <Text size="small" type="secondary">{r.email}</Text>
            </Space>
        )},
        { title: 'Job Applied', key: 'job', render: (_, r) => r.Job?.title },
        { title: 'Pipeline Status', dataIndex: 'status', key: 'status', render: (status, r) => (
            <Select 
                defaultValue={status} 
                style={{ width: 130 }} 
                onChange={(val) => handleUpdateStatus(r.id, val)}
                className={`status-select-${status.toLowerCase()}`}
            >
                {['APPLIED', 'SCREENING', 'INTERVIEW', 'OFFERED', 'SELECTED', 'REJECTED', 'HIRED'].map(s => (
                    <Option key={s} value={s}>{s}</Option>
                ))}
            </Select>
        )},
        { title: 'Interview', key: 'interview', render: (_, r) => (
            <Button size="small" icon={<CalendarOutlined />} onClick={() => { 
                setSelectedCandidate(r); 
                interviewForm.setFieldsValue({ candidateId: r.id });
                setInterviewModalVisible(true); 
            }}>Schedule</Button>
        )},
        { title: 'Resume', key: 'resume', render: (_, r) => r.resumeUrl ? (
            <Button type="link" icon={<FileTextOutlined />} onClick={() => window.open(`${api.defaults.baseURL}${r.resumeUrl}`, '_blank')}>View</Button>
        ) : 'N/A' }
    ];

    const interviewColumns = [
        { title: 'Date & Time', dataIndex: 'scheduledAt', key: 'time', render: d => moment(d).format('MMM DD, YYYY hh:mm A') },
        { title: 'Candidate', key: 'candidate', render: (_, r) => r.Candidate?.name },
        { title: 'Interviewer', key: 'interviewer', render: (_, r) => r.Interviewer?.profile?.name || 'N/A' },
        { title: 'Meeting', dataIndex: 'meetingLink', key: 'link', render: link => link ? (
            <Button type="link" icon={<EyeOutlined />} onClick={() => window.open(link, '_blank')}>Join</Button>
        ) : 'In-person' },
        { title: 'Status', dataIndex: 'status', key: 'status', render: s => (
            <Tag color={s === 'SCHEDULED' ? 'blue' : s === 'COMPLETED' ? 'green' : 'volcano'}>{s}</Tag>
        )}
    ];

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sidebar collapsed={collapsed} />
            <Layout style={{ marginLeft: collapsed ? 80 : 200, height: '100vh', overflow: 'hidden' }}>
                <Header style={{ padding: 0, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 90 }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                            className: 'trigger',
                            onClick: () => setCollapsed(!collapsed),
                            style: { fontSize: '18px', padding: '0 24px' }
                        })}
                        <Title level={4} style={{ margin: 0 }}>Recruitment System (ATS)</Title>
                    </div>
                    <Menu
                        theme="light"
                        mode="horizontal"
                        items={[
                            {
                                key: 'logout',
                                icon: <LogoutOutlined />,
                                label: 'Logout',
                                onClick: handleLogout
                            }
                        ]}
                        style={{ borderBottom: 'none' }}
                    />
                </Header>

                <Content style={{ margin: '24px 16px', padding: 0, background: '#f5f5f5', height: 'calc(100vh - 64px)', overflow: 'auto' }}>
                    <div style={{ padding: '24px' }}>
                        <Row justify="end" style={{ marginBottom: '16px' }}>
                            <Space>
                                <Button type="primary" icon={<PlusOutlined />} onClick={() => setJobModalVisible(true)}>Post New Job</Button>
                                <Button icon={<TeamOutlined />} onClick={() => setCandidateModalVisible(true)}>Add Candidate</Button>
                            </Space>
                        </Row>
                        <Card style={{ borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                        <Tabs activeKey={activeTab} onChange={setActiveTab}>
                            <TabPane tab={<span><FileTextOutlined />Job Postings</span>} key="jobs">
                                <Table columns={jobColumns} dataSource={jobs} loading={loading} rowKey="id" />
                            </TabPane>
                            <TabPane tab={<span><TeamOutlined />Candidates</span>} key="candidates">
                                <Table columns={candidateColumns} dataSource={candidates} loading={loading} rowKey="id" />
                            </TabPane>
                            <TabPane tab={<span><CalendarOutlined />Interviews</span>} key="interviews">
                                <Table columns={interviewColumns} dataSource={interviews} loading={loading} rowKey="id" />
                            </TabPane>
                        </Tabs>
                    </Card>
                </div>
                </Content>

                {/* Job Modal */}
                <Modal 
                    title={selectedJob ? "Edit Job Posting" : "New Job Posting"} 
                    visible={jobModalVisible} 
                    onCancel={() => { setJobModalVisible(false); setSelectedJob(null); form.resetFields(); }}
                    onOk={() => form.submit()}
                    width={700}
                >
                    <Form form={form} layout="vertical" onFinish={handleCreateJob}>
                        <Row gutter={16}>
                            <Col span={16}><Form.Item name="title" label="Job Title" rules={[{ required: true }]}><Input placeholder="e.g. Senior Backend Developer" /></Form.Item></Col>
                            <Col span={8}><Form.Item name="jobType" label="Job Type" initialValue="Full-time"><Select><Option value="Full-time">Full-time</Option><Option value="Part-time">Part-time</Option><Option value="Contract">Contract</Option><Option value="Internship">Internship</Option></Select></Form.Item></Col>
                        </Row>
                        <Form.Item name="location" label="Location"><Input placeholder="e.g. Remote, Mumbai, New York" /></Form.Item>
                        <Form.Item name="description" label="Description" rules={[{ required: true }]}><Input.TextArea rows={4} /></Form.Item>
                        <Form.Item name="requirements" label="Requirements"><Input.TextArea rows={4} /></Form.Item>
                        <Row gutter={16}>
                            <Col span={12}><Form.Item name="salaryRange" label="Salary Range"><Input placeholder="e.g. ₹10L - ₹15L" /></Form.Item></Col>
                            <Col span={12}><Form.Item name="status" label="Status" initialValue="OPEN"><Select><Option value="OPEN">Open</Option><Option value="DRAFT">Draft</Option><Option value="CLOSED">Closed</Option></Select></Form.Item></Col>
                        </Row>
                    </Form>
                </Modal>

                {/* Candidate Modal */}
                <Modal 
                    title="Add Individual Candidate" 
                    visible={candidateModalVisible} 
                    onCancel={() => setCandidateModalVisible(false)}
                    onOk={() => candidateForm.submit()}
                >
                    <Form form={candidateForm} layout="vertical" onFinish={handleAddCandidate}>
                        <Form.Item name="name" label="Candidate Name" rules={[{ required: true }]}><Input /></Form.Item>
                        <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}><Input /></Form.Item>
                        <Form.Item name="phone" label="Phone" rules={[{ required: true }]}><Input /></Form.Item>
                        <Form.Item name="jobId" label="Job Applying For" rules={[{ required: true }]}>
                            <Select placeholder="Select a job position">
                                {jobs.filter(j => j.status === 'OPEN').map(j => (
                                    <Option key={j.id} value={j.id}>{j.title}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item 
                            name="resume" 
                            label="Resume (PDF/Doc)"
                            valuePropName="fileList"
                            getValueFromEvent={(e) => {
                                if (Array.isArray(e)) return e;
                                return e && e.fileList;
                            }}
                        >
                            <Upload beforeUpload={() => false} maxCount={1}>
                                <Button icon={<UploadOutlined />}>Select Resume</Button>
                            </Upload>
                        </Form.Item>
                    </Form>
                </Modal>

                {/* Interview Modal */}
                <Modal 
                    title="Schedule Interview" 
                    visible={interviewModalVisible} 
                    onCancel={() => setInterviewModalVisible(false)}
                    onOk={() => interviewForm.submit()}
                >
                    <Form form={interviewForm} layout="vertical" onFinish={handleScheduleInterview}>
                        <Form.Item name="candidateId" label="Candidate" hidden><Input /></Form.Item>
                        <Form.Item label="Candidate Name">
                            <Text strong>{selectedCandidate?.name}</Text>
                        </Form.Item>
                        <Form.Item name="interviewerId" label="Interviewer" rules={[{ required: true }]}>
                            <Select placeholder="Select staff member">
                                {interviewers.map(s => (
                                    <Option key={s.id} value={s.id}>{s.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Row gutter={16}>
                            <Col span={14}><Form.Item name="scheduledAt" label="Date & Time" rules={[{ required: true }]}><DatePicker showTime style={{ width: '100%' }} /></Form.Item></Col>
                            <Col span={10}><Form.Item name="durationMinutes" label="Duration" initialValue={30}><Input suffix="mins" type="number" /></Form.Item></Col>
                        </Row>
                        <Form.Item name="meetingLink" label="Online Meeting Link (Optional)"><Input placeholder="Zoom, GMeet, Teams link" /></Form.Item>
                        <Form.Item name="notes" label="Notes for Interviewer"><Input.TextArea /></Form.Item>
                    </Form>
                </Modal>
            </Layout>
            <style jsx>{`
                .status-select-applied .ant-select-selector { border-color: #d9d9d9 !important; }
                .status-select-screening .ant-select-selector { border-color: #1890ff !important; }
                .status-select-interview .ant-select-selector { border-color: #722ed1 !important; }
                .status-select-offered .ant-select-selector { border-color: #faad14 !important; }
                .status-select-selected .ant-select-selector { border-color: #52c41a !important; }
                .status-select-hired .ant-select-selector { border-color: #3f8600 !important; color: #3f8600 !important; }
                .status-select-rejected .ant-select-selector { border-color: #f5222d !important; color: #f5222d !important; }
            `}</style>
        </Layout>
    );
};

export default Recruitment;
